import {
  GET_THREAD_QUERY,
  GET_THREADS_AGGREGATE_QUERY,
  RootEntityType,
  Thread,
  ThreadStatus,
  ThreadUser,
  useCreateThreadChannel,
  useUpdateThread,
  useUpdateThreadMembers,
} from '@schooly/api';
import { useNotifications } from '@schooly/components/notifications';
import { useQueryClient } from '@tanstack/react-query';
import {
  createContext,
  FC,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';
import { Channel, DefaultGenerics, StreamChat } from 'stream-chat';
import { Chat } from 'stream-chat-react';

import { CHANNEL_TYPE } from './constants';
import { useConnectionProvider } from './useConnectionProvider';

export type ThreadParams = {
  schoolId: string;
  rootEntityType: RootEntityType;
  rootEntityId: string;
};

export type ThreadsContextProps = {
  client: StreamChat<DefaultGenerics> | null;
  isLoading: boolean;
  connectedUser?: ThreadUser;
  selectedThread?: Thread;
  selectThread: (thread: Thread) => Promise<void>;
  addSpectator: (thread: Thread, spectator: ThreadUser) => Promise<Thread>;
  addMember: (thread: Thread, member: ThreadUser) => Promise<Thread>;
  updateThreadStatus: (thread: Thread, status: ThreadStatus) => Promise<void>;
  connectUser: (user: ThreadUser) => Promise<void>;
  isChannelCreating: boolean;
  createThreadChannel: (thread: Thread) => Promise<Thread>;
  isThreadMembersUpdating: boolean;
  isUserConnecting: boolean;
  streamChannel: Channel | undefined;
  isChannelLoading: boolean;
  hasConnection: boolean;
  clearThread: () => Promise<void>;
};

export const ThreadsContext = createContext<ThreadsContextProps>({
  client: null,
  isLoading: false,
  connectedUser: undefined,
  selectedThread: undefined,
  selectThread: async () => {},
  addSpectator: async () => ({}) as Thread,
  addMember: async () => ({}) as Thread,
  updateThreadStatus: async () => {},
  connectUser: async () => {},
  createThreadChannel: async () => ({}) as Thread,
  isChannelCreating: false,
  isThreadMembersUpdating: false,
  isUserConnecting: false,
  streamChannel: undefined,
  isChannelLoading: false,
  hasConnection: false,
  clearThread: async () => {},
});

type WithThreadsProps = PropsWithChildren<{
  schoolId: string;
  rootEntityType: RootEntityType;
  rootEntityId: string;
  apiKey: string;
}>;

export const WithThreads: FC<WithThreadsProps> = ({
  children,
  rootEntityType,
  rootEntityId,
  schoolId,
  apiKey,
}) => {
  const client = useRef<StreamChat<DefaultGenerics>>(new StreamChat(apiKey));
  const [connectedUser, setConnectedUser] = useState<ThreadUser>();
  const [thread, setThread] = useState<Thread>();
  const [streamChannel, setStreamChannel] = useState<Channel>();
  const [isUserConnecting, setIsUserConnecting] = useState(false);
  const [isChannelLoading, setIsChannelLoading] = useState(false);

  const queryClient = useQueryClient();

  const createThreadChannelMutation = useCreateThreadChannel();
  const updateThreadMembersMutation = useUpdateThreadMembers();
  const updateThreadMutation = useUpdateThread();
  const { showError } = useNotifications();

  const { tokenProvider, hasConnection } = useConnectionProvider({
    schoolId,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const initStreamChannel = useCallback(
    async (channelId: string) => {
      try {
        setIsChannelLoading(true);
        await streamChannel?.stopWatching();
        const channelInstance = client.current.channel(CHANNEL_TYPE, channelId);
        await channelInstance.watch({ messages: { limit: 300 } });

        setStreamChannel(channelInstance);
      } catch (error) {
        console.error('Error getting a channel', error);
      } finally {
        setIsChannelLoading(false);
      }
    },
    [client, streamChannel],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const selectThread = useCallback(
    async (thread: Thread) => {
      if (!thread.channel || !hasConnection) return;
      await initStreamChannel(thread.channel.id);
      setThread(thread);
    },
    [streamChannel, client],
  );

  const clearThread = useCallback(async () => {
    await streamChannel?.stopWatching();
    setStreamChannel(undefined);
    setThread(undefined);
  }, [streamChannel]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const connectUser = useCallback(
    async (user: ThreadUser) => {
      if (connectedUser) return;

      try {
        setIsUserConnecting(true);

        await client.current.connectUser(
          {
            id: user.user_id,
          },
          tokenProvider,
        );
        setConnectedUser(user);
      } catch (error) {
        throw new Error(`Failed to connect user: ${error}`);
      } finally {
        setIsUserConnecting(false);
      }
    },
    [client, connectedUser, tokenProvider],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const addSpectator = useCallback(
    async (thread: Thread, spectator: ThreadUser) => {
      const updatedThread = await updateThreadMembersMutation.mutateAsync(
        {
          thread_id: thread.id,
          spectators: [spectator],
          school_id: schoolId,
        },
        {
          onError: showError,
        },
      );

      return updatedThread;
    },
    [updateThreadMembersMutation, schoolId, rootEntityType, rootEntityId, showError],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const addMember = useCallback(
    async (thread: Thread, member: ThreadUser) => {
      const updatedThread = await updateThreadMembersMutation.mutateAsync(
        {
          thread_id: thread.id,
          new_members: [member],
          school_id: schoolId,
        },
        {
          onError: showError,
        },
      );

      return updatedThread;
    },
    [updateThreadMembersMutation, schoolId, rootEntityType, rootEntityId, showError],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const createThreadChannel = useCallback(
    async (thread: Thread) => {
      return createThreadChannelMutation.mutateAsync(
        {
          thread_id: thread.id,
          school_id: schoolId,
        },
        {
          onError: showError,
        },
      );
    },
    [createThreadChannelMutation, schoolId, rootEntityType, rootEntityId, showError],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  const updateThreadStatus = useCallback(
    async (thread: Thread, status: ThreadStatus) => {
      await updateThreadMutation.mutateAsync(
        {
          root_entity_type: rootEntityType,
          root_entity_id: rootEntityId,
          thread_ids: [thread.id],
          school_id: schoolId,
          status,
        },
        {
          onError: showError,
        },
      );

      queryClient.invalidateQueries([
        GET_THREAD_QUERY,
        {
          school_id: schoolId,
          thread_id: thread.id,
        },
      ]);

      queryClient.invalidateQueries([
        GET_THREADS_AGGREGATE_QUERY,
        {
          school_id: schoolId,
          root_entity_type: rootEntityType,
          root_entity_id: rootEntityId,
        },
      ]);
    },
    [updateThreadMutation, schoolId, rootEntityType, rootEntityId, queryClient],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: Legacy code
  useEffect(
    () => () => {
      client.current.disconnectUser();
      setConnectedUser(undefined);
      streamChannel?.stopWatching();
      setStreamChannel(undefined);
    },
    [],
  );

  return (
    <ThreadsContext.Provider
      value={{
        client: client.current,
        isLoading: false,
        isChannelLoading,
        selectedThread: thread,
        selectThread,
        streamChannel,
        addSpectator,
        addMember,
        updateThreadStatus,
        connectedUser,
        connectUser,
        createThreadChannel,
        isUserConnecting,
        isChannelCreating: createThreadChannelMutation.isLoading,
        isThreadMembersUpdating: updateThreadMembersMutation.isLoading,
        hasConnection,
        clearThread,
      }}
    >
      <Chat client={client.current}>{children}</Chat>
    </ThreadsContext.Provider>
  );
};

export const useThreads = () => {
  return useContext(ThreadsContext);
};
